import React, { useState } from 'react';

import classNames from 'classnames';

import Tooltip from 'rc-tooltip';

import type { ColorChangeHandler } from 'react-color';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/pro-solid-svg-icons';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

import { useUnsplash } from 'Hooks/useUnsplash';

import { FlexBox, Spacer, TextInput } from '@eltoro-ui/components';
import { PhotoButton } from '@eltoro-ui/creative_generator/Components/UI/BackgroundImageTab/components';

import ColorPicker from 'Components/CanvasEditor/components/ColorPicker';
import ColorField from 'Pages/CampaignCreation/components/CampaignCreatives/_components/MagicCreativeGenerator/components/MagicCreativeContent/components/FillDetails/components/BannerBackgroundColorPicker/components/ColorField';

import type { CanvasBackground } from 'Components/CanvasEditor/types';

const colorOptions = [
  '#F0ECE9',
  '#C6CDE4',
  '#D1E8E2',
  '#F1DED0',
  '#F87171',
  '#FB923C',
  '#FBBF24',
  '#A3E635',
  '#34D399',
  '#22D3EE',
  '#60A5FA',
  '#818CF8',
  '#A78BFA',
  '#F472B6',
  '#000000',
];

interface BackgroundProps {
  background: CanvasBackground;
  onBackgroundChange: (background: CanvasBackground) => void;
}

export default function Background({ background, onBackgroundChange }: BackgroundProps) {
  const { photos, search, handleSearch } = useUnsplash();

  const [colorPickerValue, setColorPickerValue] = useState<string>(background.value);

  const onChangeColorPicker: ColorChangeHandler = ({ hex }) => {
    setColorPickerValue(hex);
    onBackgroundChange({ type: 'color', value: hex });
  };

  const handleBackgroundImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files || files.length === 0) return;

    const [file] = files;
    const reader = new FileReader();

    reader.onload = event => {
      if (event.target && typeof event.target.result === 'string') {
        onBackgroundChange({
          type: 'image',
          value: event.target.result,
        });
      }
    };

    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleColorSelect = (color: string) => {
    onBackgroundChange({
      type: 'color',
      value: color,
    });
  };

  return (
    <FlexBox flexDirection="column">
      <FlexBox flexDirection="column" gap="0.75rem">
        <span className="section-title">Background Color</span>
        <FlexBox flexWrap="wrap">
          <ColorPicker
            type="background"
            color={colorPickerValue}
            isSelected={background.value === colorPickerValue}
            onChangeColorPicker={onChangeColorPicker}
            onBackgroundChange={onBackgroundChange}
          />
          {colorOptions.map(color => (
            <ColorField
              key={color}
              color={color}
              isSelected={background.type === 'color' && background.value === color}
              onSelectColor={color => handleColorSelect(color)}
            />
          ))}
        </FlexBox>
      </FlexBox>
      <FlexBox flexDirection="column" gap="0.75rem">
        <FlexBox alignItems="center" gap="0.5rem">
          <span className="section-title">Background Image</span>
          <Tooltip
            placement="topLeft"
            trigger="hover"
            overlayClassName={classNames('audience-tooltip', 'save-audience-info')}
            showArrow
            align={{
              offset: [-15, -3],
              targetOffset: [-6, 0],
            }}
            overlay={
              <span>
                Images provided by Unsplash. To learn more visit{' '}
                <a
                  href="https://unsplash.com/license"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: 'inherit' }}
                >
                  License | Unsplash
                </a>
              </span>
            }
            getTooltipContainer={() => document.body}
          >
            <span style={{ cursor: 'pointer', display: 'flex' }}>
              <FontAwesomeIcon
                icon={faExclamationCircle}
                color="#AAB2B5"
                style={{ fontSize: 20 }}
              />
            </span>
          </Tooltip>
        </FlexBox>
        <div className="image-uploader">
          <label htmlFor="upload-background" className="upload-button">
            <FontAwesomeIcon icon={faUpload} />
            <span>Upload Background</span>
            <input
              id="upload-background"
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleBackgroundImageUpload}
            />
          </label>
        </div>
      </FlexBox>
      <FlexBox flexDirection="column" gap="0.75rem">
        <TextInput
          className="unsplash-image-search-input"
          value={search}
          onChange={e => handleSearch(e.target.value)}
          icon={<FontAwesomeIcon icon={faSearch} />}
        />
        <FlexBox flexDirection="column">
          {photos.map(photo => (
            <React.Fragment key={photo.id}>
              <PhotoButton
                photo={photo}
                onClick={() => {
                  onBackgroundChange({
                    type: 'image',
                    value: photo.urls.regular,
                  });
                }}
              />
              <Spacer />
            </React.Fragment>
          ))}
        </FlexBox>
      </FlexBox>
    </FlexBox>
  );
}
